import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { ElMessage } from 'element-plus'

const routes: RouteRecordRaw[] = [
    {
        path: '/login',
        name: 'Login',
        component: () => import('@/views/Login.vue'),
        meta: {
            title: '登录',
            requiresAuth: false
        }
    },
    {
        path: '/',
        redirect: '/dashboard'
    },
    {
        path: '/dashboard',
        name: 'Dashboard',
        component: () => import('@/views/Layout.vue'),
        meta: {
            title: '控制台',
            requiresAuth: true
        },
        children: [
            {
                path: '',
                name: 'DashboardHome',
                component: () => import('@/views/Dashboard.vue'),
                meta: {
                    title: '控制台',
                    requiresAuth: true
                }
            },
            {
                path: '/ai-functions',
                name: 'AIFunctions',
                component: () => import('@/views/AIFunctions.vue'),
                meta: {
                    title: 'AI功能列表',
                    requiresAuth: true,
                    permissions: ['ai:function:view']
                }
            },
            {
                path: '/ai-model-detail/:id',
                name: 'AIModelDetail',
                component: () => import('@/views/AIModelDetail.vue'),
                meta: {
                    title: 'AI模型详情',
                    requiresAuth: true,
                    permissions: ['ai:function:view']
                }
            },
            {
                path: '/model-management',
                name: 'ModelManagement',
                component: () => import('@/views/ModelManagement.vue'),
                meta: {
                    title: '模型管理',
                    requiresAuth: true,
                    permissions: ['ai:model:view']
                }
            },
            {
                path: '/usage-stats',
                name: 'UsageStats',
                component: () => import('@/views/UsageStats.vue'),
                meta: {
                    title: '详细使用统计',
                    requiresAuth: true,
                    permissions: ['usage:stats:view']
                }
            },
            {
                path: '/user-info',
                name: 'UserInfo',
                component: () => import('@/views/UserInfo.vue'),
                meta: {
                    title: '用户信息',
                    requiresAuth: true
                }
            },
            {
                path: '/system-settings',
                name: 'SystemSettings',
                component: () => import('@/views/SystemSettings.vue'),
                meta: {
                    title: '系统设置',
                    requiresAuth: true,
                    permissions: ['system:settings:view']
                }
            },
            {
                path: '/help-support',
                name: 'HelpSupport',
                component: () => import('@/views/HelpSupport.vue'),
                meta: {
                    title: '帮助与支持',
                    requiresAuth: true
                }
            }
        ]
    },
    {
        path: '/:pathMatch(.*)*',
        name: 'NotFound',
        component: () => import('@/views/NotFound.vue'),
        meta: {
            title: '页面不存在'
        }
    }
]

const router = createRouter({
    history: createWebHistory(),
    routes
})

// 路由守卫
router.beforeEach(async (to, _from, next) => {
    const userStore = useUserStore()

    // 设置页面标题
    if (to.meta?.title) {
        document.title = `${to.meta.title} - AI中台管理系统`
    }

    // 检查是否需要登录
    if (to.meta?.requiresAuth && !userStore.isLoggedIn) {
        ElMessage.warning('请先登录')
        next('/login')
        return
    }

    // 如果已登录但访问登录页，重定向到首页
    if (to.name === 'Login' && userStore.isLoggedIn) {
        next('/dashboard')
        return
    }

    // 检查权限
    console.log(`Checking permissions for route name=${to.name?.toString()} controll:${import.meta.env.VITE_APP_PERMISSION_CONTROL}`, to.meta?.permissions, userStore.userInfo)
    if (to.meta?.permissions && userStore.userInfo && Array.isArray(to.meta.permissions)) {
        const hasPermission = import.meta.env.VITE_APP_PERMISSION_CONTROL || (to.meta.permissions as string[]).some((permission: string) =>
            userStore.hasPermission(permission)
        )

        if (!hasPermission) {
            ElMessage.error('没有权限访问此页面')
            next('/dashboard')
            return
        }
    }

    next()
})

export default router
